
import React from 'react';
import { Download, Calendar } from 'lucide-react';
import { RentalScheduleItem } from '../../utils/calculationUtils';
import { Button } from '../../components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';

interface PaymentScheduleSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  schedule: RentalScheduleItem[];
  rentalCalculationBasis: 'preGstDiscounting' | 'postGstDiscounting' | 'postTdsDiscounting';
}

const PaymentScheduleSidebar: React.FC<PaymentScheduleSidebarProps> = ({ 
  isOpen, 
  onClose, 
  schedule 
}) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
    }).format(value);
  };

  const exportToCSV = () => {
    const headers = [
      'Sr.No', 
      'Payment Type', 
      'Due Date',
      'Factor (Days)',
      'Total Rental Amount (Post TDS)',
      'Interest', 
      'Principal', 
      'Principal Outstanding'
    ];
    
    const csvContent = [
      headers.join(','),
      ...schedule.map(item => [
        item.srNo,
        item.paymentType,
        item.dueDate || '',
        item.factor || 0,
        item.rentalPostTds.toFixed(2),
        item.interest.toFixed(2),
        item.principal.toFixed(2),
        item.principalOutstanding.toFixed(2)
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'rental_schedule.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="right" className="w-full sm:w-[1000px] sm:max-w-[95vw] overflow-hidden">
        <SheetHeader className="space-y-3 pb-4 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Calendar className="h-5 w-5 text-purple-600" />
              <div>
                <SheetTitle className="text-xl font-semibold text-gray-900">Payment Schedule</SheetTitle>
                <p className="text-sm text-gray-600">{schedule.length} installments with factor calculations</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              className="flex items-center gap-2 border-purple-200 text-purple-700 hover:bg-purple-50"
              onClick={exportToCSV}
            >
              <Download className="h-4 w-4" />
              Export CSV
            </Button>
          </div>
        </SheetHeader>
        
        <div className="mt-6 h-full overflow-hidden flex flex-col">
          <div className="flex-1 overflow-auto mb-[10%]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-center">Sr.No</TableHead>
                  <TableHead>Payment Type</TableHead>
                  <TableHead className="text-center">Due Date</TableHead>
                  <TableHead className="text-center">Factor (Days)</TableHead>
                  <TableHead className="text-right">Total Rental</TableHead>
                  <TableHead className="text-right">Interest</TableHead>
                  <TableHead className="text-right">Principal</TableHead>
                  <TableHead className="text-right">Principal Outstanding</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {schedule.map((item) => (
                  <TableRow key={item.srNo} className="hover:bg-gray-50">
                    <TableCell className="text-center font-medium">{item.srNo}</TableCell>
                    <TableCell className="font-medium">{item.paymentType}</TableCell>
                    <TableCell className="text-center">{item.dueDate || 'N/A'}</TableCell>
                    <TableCell className="text-center font-medium text-blue-600">
                      {item.factor || 0}
                    </TableCell>
                    <TableCell className="text-right font-medium text-purple-600">
                      {formatCurrency(item.rentalPostTds)}
                    </TableCell>
                    <TableCell className="text-right">{formatCurrency(item.interest)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(item.principal)}</TableCell>
                    <TableCell className={`text-right font-medium ${
                      item.principalOutstanding <= 0 ? 'text-green-600' : 'text-gray-900'
                    }`}>
                      {formatCurrency(item.principalOutstanding)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          
          {/* Summary Footer */}
          {/* <div className="mt-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-4 border border-purple-200 flex-shrink-0">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-center">
              <div>
                <div className="text-sm text-purple-600 font-medium">Total Interest</div>
                <div className="text-lg font-bold text-purple-900">
                  {formatCurrency(schedule.reduce((sum, item) => sum + item.interest, 0))}
                </div>
              </div>
              <div>
                <div className="text-sm text-purple-600 font-medium">Total Principal</div>
                <div className="text-lg font-bold text-purple-900">
                  {formatCurrency(schedule.reduce((sum, item) => sum + item.principal, 0))}
                </div>
              </div>
              <div>
                <div className="text-sm text-purple-600 font-medium">Average Factor</div>
                <div className="text-lg font-bold text-purple-900">
                  {schedule.length > 0 ? Math.round(schedule.reduce((sum, item) => sum + (item.factor || 0), 0) / schedule.length) : 0} days
                </div>
              </div>
              <div>
                <div className="text-sm text-purple-600 font-medium">Total Rental Payments</div>
                <div className="text-lg font-bold text-purple-900">
                  {formatCurrency(schedule.reduce((sum, item) => sum + item.rentalPostTds, 0))}
                </div>
              </div>
            </div>
          </div> */}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default PaymentScheduleSidebar;
